import React, { useState, createContext, useContext } from 'react';
import './App.css';
import IntroPanel from './IntroPanel';
import TetrisPanel from './TetrisPanel';
import MenuPanel from './MenuPanel';
import AboutPanel from './AboutPanel';
import ServicesPanel from './ServicesPanel';
import ProjectsPanel from './ProjectsPanel';
import ContactPanel from './ContactPanel';

// Panel configuration
const ALL_PANELS = {
  intro: { id: 'intro', component: IntroPanel },
  tetris: { id: 'tetris', component: TetrisPanel },
  menu: { id: 'menu', component: MenuPanel },
  about: { id: 'about', component: AboutPanel },
  services: { id: 'services', component: ServicesPanel },
  projects: { id: 'projects', component: ProjectsPanel },
  contact: { id: 'contact', component: ContactPanel },
};

// Starting panels
const DEFAULT_PANELS = {
  leftPanel: ALL_PANELS.intro,
  rightPanel: window.innerWidth <= 768 ? null : ALL_PANELS.contact,
};

// Create panel context
const PanelContext = createContext();

// Panel provider component
export function PanelProvider({ children }) {
  const [activePanels, setActivePanels] = useState(DEFAULT_PANELS);

  const showPanel = (panelId, panelName) => {
    if (!ALL_PANELS[panelId]) return;
    setActivePanels(prev => ({
      ...prev,
      [panelName]: ALL_PANELS[panelId],
    }));
  };

  const hidePanel = (panelName) => {
    setActivePanels(prev => ({
      ...prev,
      [panelName]: null,
    }));
  };

  const togglePanel = (panelId, panelName) => {
    if (activePanels[panelName] && activePanels[panelName].id === panelId) {
      hidePanel(panelName);
    } else {
      showPanel(panelId, panelName);
    }
  };

  const swapPanel = async (showId, panelName) => {
    // On mobile, always use leftPanel
    if (window.innerWidth <= 768) {
      panelName = 'leftPanel';
    }

    if (!ALL_PANELS[showId]) return;
    const currentPanel = activePanels[panelName];
    if (currentPanel && currentPanel.id === showId) return;

    // Fade out the panel being hidden
    if (currentPanel) {
      const panelToHide = document.getElementById(currentPanel.id + 'Panel');
      if (panelToHide) {
        panelToHide.style.transition = 'opacity 500ms';
        panelToHide.style.opacity = '0';
        await new Promise(resolve => setTimeout(resolve, 800));
      }
    }

    setActivePanels(prev => ({
      ...prev,
      [panelName]: ALL_PANELS[showId],
    }));
  };

  return (
    <PanelContext.Provider value={{ 
      activePanels, 
      showPanel, 
      hidePanel, 
      togglePanel,
      swapPanel,
      isPanelActive: (panelId, panelName) => activePanels[panelName] && activePanels[panelName].id === panelId
    }}>
      {children}
    </PanelContext.Provider>
  );
}

// Hook to use panel context
export function usePanels() {
  const context = useContext(PanelContext);
  if (!context) {
    throw new Error('usePanels must be used within a PanelProvider');
  }
  return context;
}

function App() {
  return (
    <PanelProvider>
      <AppContent />
    </PanelProvider>
  );
}

function AppContent() {
  const { activePanels } = usePanels();
  return (
    <div className="App-panels">
      {['leftPanel', 'rightPanel'].map(panelName => {
        const panel = activePanels[panelName];
        if (!panel) return null;
        const PanelComponent = panel.component;
        return <PanelComponent key={panel.id} {...panel.props} />;
      })}
    </div>
  );
}

export default App;
